// src/Pages/Split/SplitVisuals.jsx
// ─────────────────────────────────────────────────────────────────────────
// Blitz Split — the visual layer:
//   GlobeBackground   ambient yellow globe (InternalTransfer treatment)
//   SplitBar          live stacked proportion bar (the signature)
//   DeferredBar       hatched ghost bar for distribution-later mode
//   SplitHero         integrated header card with status chips
// ─────────────────────────────────────────────────────────────────────────

import React from "react";
import { Box, Typography, Chip, Stack, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CallSplit, Bolt, CheckCircle as CheckCircleIcon,
  Business as BusinessIcon, Person as PersonIcon, Schedule as LaterIcon,
} from "@mui/icons-material";

import Globe from "../../Dashboard/dashboard/globe";
import { parseLocalFloat } from "../../../Utils/constants";
import {
  SPLIT_YELLOW, SPLIT_YELLOW_DEEP, SPLIT_YELLOW_DARK, SPLIT_INK,
  SLICE_COLORS, fadeUp, fadeIn, fmt,
} from "./SplitTokens";

// ═══════════════════════════════════════════════════════════════════════════
export function GlobeBackground({ size = 1200 }) {
  return (
    <Box aria-hidden="true" sx={{
      pointerEvents: "none", opacity: 0.12,
      transform: "rotate3d(1, 0.3, 0, 22deg)",
      maskImage: "radial-gradient(ellipse 75% 75% at 50% 50%, black 40%, transparent 80%)",
      WebkitMaskImage: "radial-gradient(ellipse 75% 75% at 50% 50%, black 40%, transparent 80%)",
    }}>
      <Globe size={size} color={SPLIT_YELLOW} backgroundColor="transparent"
        borderColor="transparent" borderWidth={0} />
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export function SplitBar({ rows, total }) {
  if (!total || total <= 0) return null;
  const slices = rows
    .map((r, i) => ({
      key: r.rowId,
      label: r.kind === "me" ? "You" : (r.name || "—"),
      amt: parseLocalFloat(r.amount) || 0,
      color: r.kind === "me" ? SPLIT_INK : SLICE_COLORS[i % SLICE_COLORS.length],
      pre: r.blitzpayPre,
    }))
    .filter((s) => s.amt > 0);
  if (!slices.length) return null;

  return (
    <Box sx={{ animation: `${fadeIn} 0.25s ease` }}>
      <Box sx={{
        display: "flex", height: 14, borderRadius: 99, overflow: "hidden",
        border: "1px solid", borderColor: alpha(SPLIT_INK, 0.12),
        boxShadow: `inset 0 1px 2px ${alpha(SPLIT_INK, 0.08)}`,
      }}>
        {slices.map((s) => (
          <Tooltip key={s.key} arrow
            title={`${s.label} · ${fmt(s.amt)} (${((s.amt / total) * 100).toFixed(1)}%)${s.pre ? " · ⚡ BlitzPay pre-accepted" : ""}`}>
            <Box sx={{
              width: `${(s.amt / total) * 100}%`,
              bgcolor: s.color,
              transition: "width 0.35s cubic-bezier(0.4,0,0.2,1)",
              "&:not(:last-of-type)": { borderRight: "2px solid", borderColor: "background.paper" },
            }} />
          </Tooltip>
        ))}
      </Box>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        {slices.map((s) => (
          <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary",
              fontSize: "0.68rem" }} noWrap>
              {s.label} · {((s.amt / total) * 100) % 1
                ? ((s.amt / total) * 100).toFixed(1)
                : ((s.amt / total) * 100).toFixed(0)}%
              {s.pre ? " ⚡" : ""}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export function DeferredBar({ count }) {
  if (!count) return null;
  return (
    <Box sx={{ animation: `${fadeIn} 0.25s ease` }}>
      <Box sx={{ display: "flex", height: 14, borderRadius: 99, overflow: "hidden",
        border: "1px dashed", borderColor: alpha(SPLIT_YELLOW_DARK, 0.5) }}>
        {Array.from({ length: count }).map((_, i) => (
          <Box key={i} sx={{
            flex: 1,
            background: `repeating-linear-gradient(45deg, ${alpha(SPLIT_YELLOW, 0.25)}, ${alpha(SPLIT_YELLOW, 0.25)} 6px, ${alpha(SPLIT_YELLOW, 0.08)} 6px, ${alpha(SPLIT_YELLOW, 0.08)} 12px)`,
            "&:not(:last-of-type)": { borderRight: "2px solid", borderColor: "background.paper" },
          }} />
        ))}
      </Box>
      <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 600,
        color: "text.disabled", fontSize: "0.68rem" }}>
        Distribution to be assigned — everyone's on the invoice, shares come later.
      </Typography>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SplitHero — payer chip is person-first when a contact is chosen
// ═══════════════════════════════════════════════════════════════════════════
export function SplitHero({ total, payer, contact, peopleCount, deferred }) {
  const payerLabel = !payer
    ? "Pick a BlitzPay payer"
    : contact
      ? `${contact.name} · ${payer.name}`
      : `${payer.name} — pick a person`;
  const payerDone = !!payer && !!contact;

  return (
    <Box sx={{
      mb: 2.5, p: { xs: 2.5, md: 3 }, borderRadius: 4,
      background: `linear-gradient(135deg, ${SPLIT_YELLOW_DEEP} 0%, ${SPLIT_YELLOW} 60%, #FFD45E 100%)`,
      boxShadow: `0 8px 32px ${alpha(SPLIT_YELLOW, 0.35)}`,
      color: SPLIT_INK, position: "relative", overflow: "hidden",
      animation: `${fadeUp} 0.3s ease`,
    }}>
      <Box sx={{ position: "absolute", top: -40, right: -40, width: 180, height: 180,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${alpha("#fff", 0.25)} 0%, transparent 70%)`,
        pointerEvents: "none" }} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: alpha(SPLIT_INK, 0.1),
            display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(8px)" }}>
            <CallSplit sx={{ fontSize: 21, color: SPLIT_INK }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
            Blitz Split
          </Typography>
        </Box>
        {total > 0 && (
          <Box sx={{ textAlign: "right", animation: `${fadeIn} 0.2s ease` }}>
            <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums" }}>
              {fmt(total)}
            </Typography>
            {deferred && (
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.6rem",
                opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Distribution later
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.85, maxWidth: 480,
        lineHeight: 1.5, mb: 1.75 }}>
        Send one invoice to a person at a BlitzPay company and split it with your
        contractors — everyone sees their share now, and gets paid when the transfer lands.
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          icon={payerDone
            ? <CheckCircleIcon sx={{ fontSize: "13px !important", color: `${SPLIT_YELLOW} !important` }} />
            : <BusinessIcon sx={{ fontSize: "13px !important", color: `${SPLIT_INK} !important` }} />}
          label={payerLabel}
          size="small"
          sx={{ height: 24, fontSize: "0.68rem", fontWeight: 700,
            bgcolor: payerDone ? alpha(SPLIT_INK, 0.85) : alpha(SPLIT_INK, 0.12),
            color: payerDone ? SPLIT_YELLOW : SPLIT_INK, "& .MuiChip-label": { px: 1 } }} />
        <Chip
          icon={peopleCount > 0
            ? <CheckCircleIcon sx={{ fontSize: "13px !important", color: `${SPLIT_YELLOW} !important` }} />
            : <PersonIcon sx={{ fontSize: "13px !important", color: `${SPLIT_INK} !important` }} />}
          label={peopleCount > 0 ? `${peopleCount} on the split` : "Add your people"}
          size="small"
          sx={{ height: 24, fontSize: "0.68rem", fontWeight: 700,
            bgcolor: peopleCount > 0 ? alpha(SPLIT_INK, 0.85) : alpha(SPLIT_INK, 0.12),
            color: peopleCount > 0 ? SPLIT_YELLOW : SPLIT_INK, "& .MuiChip-label": { px: 1 } }} />
        <Chip
          icon={deferred
            ? <LaterIcon sx={{ fontSize: "13px !important", color: `${SPLIT_INK} !important` }} />
            : <Bolt sx={{ fontSize: "13px !important", color: `${SPLIT_INK} !important` }} />}
          label={deferred ? "Shares set later" : "Paid when it lands"}
          size="small"
          sx={{ height: 24, fontSize: "0.68rem", fontWeight: 700,
            bgcolor: alpha(SPLIT_INK, 0.12), color: SPLIT_INK,
            "& .MuiChip-label": { px: 1 } }} />
      </Stack>
    </Box>
  );
}